









































































































import {
  defineComponent,
  ref,
  useRouter,
  useContext,
  useMeta,
  onMounted,
  onUnmounted,
} from '@nuxtjs/composition-api';
import LoginComponent from '~/components/Authentication/Login.vue';
import RegistrationComponent from '~/components/Authentication/Registration.vue';
import ForgotPasswordComponent from '~/components/Authentication/ForgotPassword.vue';
import ForgotPasswordThanksComponent from '~/components/Authentication/ForgotPasswordThanks.vue';
import {
  B2BRegisterFormFields,
  ForgotPasswordFormFields,
  FormName,
  LoginFormFields,
  RegisterFormFields,
} from '~/modules/customer/components/LoginModal/forms/types';
import { useUser } from '~/modules/customer/composables/useUser';
import { useCart } from '~/modules/checkout/composables/useCart';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import {
  useForgotPassword,
  useUiNotification,
  useRecaptcha,
  useUiHelpers,
  CustomerAddressInput,
} from '~/composables';
import { getMetaInfo, getUrlMeta } from '~/helpers/getMetaInfo';
import { useUserCom } from '~/modules/usercom/composables';
import B2BRegistrationComponent from '~/components/Authentication/B2BRegistrationComponent.vue';

export default defineComponent({
  name: 'Authentication',
  components: {
    LoginComponent,
    RegistrationComponent,
    ForgotPasswordComponent,
    ForgotPasswordThanksComponent,
    B2BRegistrationComponent,
  },
  layout: 'auth',
  // eslint-disable-next-line consistent-return
  middleware({ redirect, app }) {
    // if a user is already logged in don't show him this page
    if (app.$vsf.$magento.config.state.getCustomerToken()) {
      return redirect('/customer/my-profile');
    }
  },
  setup() {
    const uiHelpers = useUiHelpers();
    useMeta(() => getMetaInfo({ page: getUrlMeta(uiHelpers.resolvePath()) }));

    const { app } = useContext();
    const activeTab = ref<FormName>('login');
    const loginForm = ref<LoginFormFields>({
      email: '',
      password: '',
    });
    const registerForm = ref<RegisterFormFields>({
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      shouldSubscribeToNewsletter: false,
      isWillToCreateAccountConfirmed: false,
      consent_to_rules: false,
      consent_to_email_commercials: false,
      consent_to_phone_commercials: false,
    });
    const forgotPasswordForm = ref<ForgotPasswordFormFields>({
      username: '',
    });

    const { load: loadCart } = useCart();
    const { loadItemsCount } = useWishlist();
    const {
      request: resetPassword,
      error: forgotPasswordError,
      loading: forgotPasswordLoading,
    } = useForgotPassword();
    const router = useRouter();
    const { getRecaptchaToken } = useRecaptcha();
    const {
      register,
      login,
      loading,
      user,
      load,
      loginWithToken,
      loadSocialLoginButtonConfig,
      socialButtonConfig,
      error: userError,
    } = useUser();
    const { send: sendNotification } = useUiNotification();
    const { isUserComActive, getKey } = useUserCom();

    const setActiveTab = (tab: FormName) => {
      activeTab.value = tab;
    };

    const isReturnURL = ref(!!router.currentRoute.query?.returnURL);

    const redirectAfterAction = () => {
      if (isReturnURL.value) {
        router.back();
      } else {
        const homePath = app.localeRoute({ name: 'home' });
        router.push(homePath);
      }
    };

    const onLoginFormSubmit = async (form: LoginFormFields) => {
      loginForm.value = form;
      const token = await getRecaptchaToken('login');
      await login({
        user: { ...form, ...(token ? { recaptchaToken: token } : {}) },
      });
      if (!userError.value.login) {
        await Promise.all([loadItemsCount(), loadCart(), load()]);
        app.$googleTagManager.sign({
          type: 'login',
          userId: user.value?.id,
        });
        sendNotification({
          id: Symbol('user_successfully_login'),
          message: app.i18n.t('Logged in successfully') as string,
          type: 'success',
          icon: 'check',
          persist: false,
          title: app.i18n.t('Logging') as string,
        });
        redirectAfterAction();
      }
    };
    const handleSocialLoginMessageEvent = async (e: any) => {
      if (!e.data) {
        return;
      }
      const { token, result } = e.data || {};
      if (result === 1 && token) {
        await loginWithToken({ token });
        if (!userError.value.login) {
          await Promise.all([loadItemsCount(), loadCart(), load()]);
          app.$googleTagManager.sign({
            type: 'login',
            userId: user.value?.id,
          });
          sendNotification({
            id: Symbol('user_successfully_login'),
            message: app.i18n.t('Logged in successfully') as string,
            type: 'success',
            icon: 'check',
            persist: false,
            title: app.i18n.t('Logging') as string,
          });
          redirectAfterAction();
        }
      } else if (result === 0) {
        sendNotification({
          id: Symbol('user_successfully_login'),
          message: app.i18n.t('Failed to log in') as string,
          type: 'success',
          icon: 'check',
          persist: false,
          title: app.i18n.t('Logging') as string,
        });
      }
    };
    const onSocialSubmit = (url: string) => {
      window.addEventListener(
        'message',
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleSocialLoginMessageEvent,
      );
      const link = `${url}&generateToken=true&isAjax=true`;
      window.open(link);
    };
    onMounted(async () => {
      await loadSocialLoginButtonConfig();
    });
    onUnmounted(() => {
      window.removeEventListener(
        'message',
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleSocialLoginMessageEvent,
      );
    });
    const onRegisterFormSubmit = async (form: RegisterFormFields) => {
      registerForm.value = form;
      const token = await getRecaptchaToken('register');
      await register({
        user: {
          email: form.email,
          firstname: form.firstName,
          lastname: form.lastName,
          password: form.password,
          is_subscribed: form.shouldSubscribeToNewsletter,
          consent_to_rules: form.consent_to_rules,
          consent_to_email_commercials: form.consent_to_email_commercials,
          consent_to_phone_commercials: form.consent_to_phone_commercials,
          ...(isUserComActive ? { usercom_id: getKey() } : {}),
          ...(token ? { recaptchaToken: token } : {}),
        },
      });
      if (!userError.value.register) {
        await Promise.all([loadItemsCount(), loadCart(), load()]);
        app.$googleTagManager.sign({
          type: 'sign_up',
          userId: user.value?.id,
        });
        sendNotification({
          id: Symbol('user_successfully_register'),
          message: app.i18n.t('Registered successfully') as string,
          type: 'success',
          icon: 'check',
          persist: false,
          title: app.i18n.t('Registration') as string,
        });
        redirectAfterAction();
      }
    };

    const onB2BRegisterFormSubmit = async (b2bData: B2BRegisterFormFields) => {
      const token = await getRecaptchaToken('register');
      await register({
        user: {
          ...b2bData,
          ...(isUserComActive ? { usercom_id: getKey() } : {}),
          ...(token ? { recaptchaToken: token } : {}),
        },
        isB2B: true,
      });
      if (userError.value.register) return;

      app.$googleTagManager.sign({
        type: 'sign_up',
        userId: user.value?.id,
      });
      sendNotification({
        id: Symbol('user_successfully_register'),
        message: app.i18n.t('Registered successfully. Your account is awaiting confirmation.') as string,
        type: 'success',
        icon: 'check',
        persist: false,
        title: app.i18n.t('Registration') as string,
      });
      redirectAfterAction();
    };

    const onForgotPasswordFormSubmit = async (
      form: ForgotPasswordFormFields,
    ) => {
      forgotPasswordForm.value = form;
      const token = await getRecaptchaToken('forgotPassword');
      await resetPassword({
        email: form.username,
        ...(token ? { recaptchaToken: token } : {}),
      });
      activeTab.value = 'forgotPasswordThankYou';
    };

    return {
      activeTab,
      setActiveTab,
      onSocialSubmit,
      loginForm,
      userError,
      loading,
      registerForm,
      forgotPasswordForm,
      forgotPasswordError,
      forgotPasswordLoading,
      socialButtonConfig,
      onLoginFormSubmit,
      onRegisterFormSubmit,
      onB2BRegisterFormSubmit,
      onForgotPasswordFormSubmit,
    };
  },
  head: {},
});
